import { Download, RefreshCw } from 'lucide-react';
import React from 'react';

import { Spinner } from '@components/spinner';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { I18n } from '@core/i18n';
import { injectTSDI } from '@core/tsdi';

import { useLicensesExportStore } from './store';

type Store = ReturnType<typeof useLicensesExportStore>;

interface Props {
    open: boolean;
    onClose: () => void;
    list: Store['list'];
    refresh: Store['refresh'];
}

const downloadArtifact = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
};

export const PastExportsDialog = ({ open, onClose, list, refresh }: Props) => {
    const { formatDateTime } = injectTSDI(I18n);

    React.useEffect(() => {
        if (open) {
            refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const items = list.$;

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader className="flex flex-row items-center justify-between gap-4 pr-8">
                    <DialogTitle>Past Licenses Exports</DialogTitle>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={refresh}
                        disabled={list.loading}
                    >
                        <RefreshCw />
                        Refresh
                    </Button>
                </DialogHeader>
                {list.loading ? (
                    <div className="flex justify-center py-8">
                        <Spinner />
                    </div>
                ) : !items.length ? (
                    <p className="py-4 text-sm text-muted-foreground">
                        No exports yet. Click Generate to create one.
                    </p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Year-Month</TableHead>
                                <TableHead>Generated at</TableHead>
                                <TableHead className="text-right">
                                    Download
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((artifact) => (
                                <TableRow key={artifact.key}>
                                    <TableCell>{artifact.yearMonth}</TableCell>
                                    <TableCell>
                                        {formatDateTime(artifact.lastModified)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            disabled={
                                                !artifact.downloadUrl?.startsWith(
                                                    'https://'
                                                )
                                            }
                                            onClick={() =>
                                                artifact.downloadUrl &&
                                                downloadArtifact(
                                                    artifact.downloadUrl
                                                )
                                            }
                                        >
                                            <Download />
                                            Download
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
